/**
 * SmallHouseInterior - Phase 6E
 * The private interior for `prop_def_thornhaven_small_house`. One simple,
 * functional single-room cabin interior. The room is centred on the origin so
 * it matches the definition's interior_spawn (0, 1, 2).
 *
 * Furniture is NOT authored here: it is loaded from the Phase 6D backend
 * (GET /api/properties/{owned_property_id}/furniture) and passed in via the
 * `furniture` prop, then rendered at each piece's server-authoritative
 * transform. A brand-new property simply has an empty room.
 *
 * The exit doorway (front/+Z wall) is wired to /api/properties/exit by the
 * `small-house-exit` interaction registered in EnhancedGameWorld.
 */

import { RigidBody, CuboidCollider } from '@react-three/rapier';

const COLORS = {
  floor: '#5d4030',
  wall: '#6d4c35',
  beam: '#3e2a18',
  ceiling: '#4a3425',
  window: '#ffe39a',
  hearthStone: '#8d8d8d',
  fire: '#ff6b35',
  furniture: '#6b4a2e',
};

const ROOM = {
  hx: 4,            // X half-extent
  hz: 4,            // Z half-extent
  height: 3,
  wallT: 0.3,
  doorHalfWidth: 0.7,
  doorHeight: 2.2,
};

/** One placed furniture piece rendered at its server transform (visual only). */
function FurniturePiece({ placement }) {
  const t = placement.transform || {};
  const p = t.position || { x: 0, y: 0, z: 0 };
  const r = t.rotation || { x: 0, y: 0, z: 0 };
  const s = t.scale || { x: 1, y: 1, z: 1 };
  const label = placement.catalog?.name || placement.asset_id || 'Item';
  return (
    <group
      position={[p.x || 0, p.y || 0, p.z || 0]}
      rotation={[0, r.y || 0, 0]}
      scale={[s.x || 1, s.y || 1, s.z || 1]}
      name={`furniture-${label}`}
    >
      <mesh position={[0, 0.4, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.8, 0.8, 0.8]} />
        <meshStandardMaterial color={COLORS.furniture} roughness={0.85} />
      </mesh>
    </group>
  );
}

export default function SmallHouseInterior({ furniture = [] }) {
  const { hx, hz, height: H, wallT: T, doorHalfWidth: DHW, doorHeight: DH } = ROOM;
  const frontSeg = hx - DHW; // each side of the door on the +Z wall

  return (
    <group>
      {/* Lighting -- warm, enclosed */}
      <ambientLight intensity={0.55} color="#ffd9a0" />
      <pointLight position={[0, H - 0.4, 0]} intensity={18} distance={16} color="#ffcf8a" castShadow />
      <pointLight position={[-hx + 1, 1.2, -hz + 1]} intensity={10} distance={8} color={COLORS.fire} />

      {/* Floor */}
      <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[hx * 2, hz * 2]} />
        <meshStandardMaterial color={COLORS.floor} roughness={0.95} />
      </mesh>
      {/* Ceiling */}
      <mesh position={[0, H, 0]} rotation={[Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[hx * 2, hz * 2]} />
        <meshStandardMaterial color={COLORS.ceiling} roughness={0.95} />
      </mesh>
      {/* Ceiling beams */}
      {[-2, 0, 2].map((x, i) => (
        <mesh key={i} position={[x, H - 0.15, 0]} castShadow>
          <boxGeometry args={[0.18, 0.18, hz * 2]} />
          <meshStandardMaterial color={COLORS.beam} roughness={0.9} />
        </mesh>
      ))}

      {/* Walls (visual) */}
      {/* Back (-Z) */}
      <mesh position={[0, H / 2, -hz]} receiveShadow castShadow>
        <boxGeometry args={[hx * 2, H, T]} />
        <meshStandardMaterial color={COLORS.wall} roughness={0.95} />
      </mesh>
      {/* Left (-X) */}
      <mesh position={[-hx, H / 2, 0]} receiveShadow castShadow>
        <boxGeometry args={[T, H, hz * 2]} />
        <meshStandardMaterial color={COLORS.wall} roughness={0.95} />
      </mesh>
      {/* Right (+X) */}
      <mesh position={[hx, H / 2, 0]} receiveShadow castShadow>
        <boxGeometry args={[T, H, hz * 2]} />
        <meshStandardMaterial color={COLORS.wall} roughness={0.95} />
      </mesh>
      {/* Front (+Z) split around the door */}
      <mesh position={[-(DHW + frontSeg / 2), H / 2, hz]} receiveShadow castShadow>
        <boxGeometry args={[frontSeg, H, T]} />
        <meshStandardMaterial color={COLORS.wall} roughness={0.95} />
      </mesh>
      <mesh position={[DHW + frontSeg / 2, H / 2, hz]} receiveShadow castShadow>
        <boxGeometry args={[frontSeg, H, T]} />
        <meshStandardMaterial color={COLORS.wall} roughness={0.95} />
      </mesh>
      <mesh position={[0, DH + (H - DH) / 2, hz]} receiveShadow castShadow>
        <boxGeometry args={[DHW * 2, H - DH, T]} />
        <meshStandardMaterial color={COLORS.wall} roughness={0.95} />
      </mesh>
      {/* Door frame + glow so the exit reads clearly */}
      {[-DHW, DHW].map((x, i) => (
        <mesh key={i} position={[x, DH / 2, hz]} castShadow>
          <boxGeometry args={[0.14, DH, T + 0.1]} />
          <meshStandardMaterial color={COLORS.beam} roughness={0.8} />
        </mesh>
      ))}

      {/* Window on back wall */}
      <mesh position={[1.5, 1.7, -hz + 0.02]}>
        <planeGeometry args={[1.1, 0.9]} />
        <meshStandardMaterial color={COLORS.window} emissive={COLORS.window} emissiveIntensity={0.45} roughness={0.5} />
      </mesh>

      {/* Stone hearth in the back-left corner */}
      <group position={[-hx + 0.9, 0, -hz + 0.9]}>
        <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
          <boxGeometry args={[1.4, 1.0, 1.4]} />
          <meshStandardMaterial color={COLORS.hearthStone} roughness={1} />
        </mesh>
        <mesh position={[0, 0.35, 0.35]}>
          <boxGeometry args={[0.7, 0.5, 0.3]} />
          <meshStandardMaterial color={COLORS.fire} emissive={COLORS.fire} emissiveIntensity={1.2} />
        </mesh>
      </group>

      {/* Placed furniture from the Phase 6D backend (empty for a fresh property) */}
      {furniture.map((f) => (
        <FurniturePiece key={f.id || f.placement_id} placement={f} />
      ))}

      {/* ===================== COLLISION ===================== */}
      <RigidBody type="fixed" colliders={false}>
        {/* Floor + ceiling */}
        <CuboidCollider args={[hx, 0.1, hz]} position={[0, -0.1, 0]} />
        <CuboidCollider args={[hx, 0.1, hz]} position={[0, H + 0.1, 0]} />
        {/* Walls */}
        <CuboidCollider args={[hx, H / 2, T / 2]} position={[0, H / 2, -hz]} />
        <CuboidCollider args={[T / 2, H / 2, hz]} position={[-hx, H / 2, 0]} />
        <CuboidCollider args={[T / 2, H / 2, hz]} position={[hx, H / 2, 0]} />
        {/* Front wall split around door + lintel */}
        <CuboidCollider args={[frontSeg / 2, H / 2, T / 2]} position={[-(DHW + frontSeg / 2), H / 2, hz]} />
        <CuboidCollider args={[frontSeg / 2, H / 2, T / 2]} position={[DHW + frontSeg / 2, H / 2, hz]} />
        <CuboidCollider args={[DHW, (H - DH) / 2, T / 2]} position={[0, DH + (H - DH) / 2, hz]} />
      </RigidBody>
    </group>
  );
}
